import type { ApiResponse } from './types';

/**
 * Authenticated API client. Every request carries a fresh App Bridge session
 * token (JWT) as a Bearer header; the Worker verifies it and (on first call)
 * installs the app via Token Exchange.
 */
async function getSessionToken(): Promise<string> {
  if (typeof window !== 'undefined' && window.shopify?.idToken) {
    return window.shopify.idToken();
  }
  throw new Error('App Bridge not available');
}

type Query = Record<string, string | number | undefined | null>;

/** Default per-request timeout — a hung request aborts instead of spinning forever. */
const REQUEST_TIMEOUT_MS = 20_000;

/** Structured API error so callers can distinguish offline/timeout/4xx/5xx. */
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`/api${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/** fetch with an abortable timeout; maps network/timeout failures to ApiError. */
async function timedFetch(url: string, init: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw new ApiError('The request timed out. Please try again.', 0, 'timeout');
    throw new ApiError('Network error — check your connection and try again.', 0, 'network');
  } finally {
    clearTimeout(timer);
  }
}

async function request<T>(
  path: string,
  { method = 'GET', body, query }: { method?: string; body?: unknown; query?: Query } = {},
): Promise<ApiResponse<T>> {
  const token = await getSessionToken();
  const res = await timedFetch(buildUrl(path, query), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) throw new ApiError('Session expired, please reload', 401, 'unauthorized');

  const json = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (!res.ok || json.ok === false) {
    throw new ApiError(json?.error?.message || `Request failed (${res.status})`, res.status);
  }
  return json;
}

/** Fetch a binary response (e.g. a PDF) with the same session-token auth. */
async function requestBlob(path: string, query?: Query): Promise<Blob> {
  const token = await getSessionToken();
  const res = await timedFetch(buildUrl(path, query), { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    // Try to surface a JSON error body (the server sends one on failure) rather
    // than a bare status.
    const json = (await res.json().catch(() => null)) as ApiResponse<unknown> | null;
    throw new ApiError(json?.error?.message || `Request failed (${res.status})`, res.status);
  }
  return res.blob();
}

// In-flight GET de-duplication: two components requesting the same URL at the
// same time share one network request (and its result). Safe — the entry is
// dropped as soon as the request settles, so it never serves a stale response,
// it only coalesces concurrent identical GETs.
const inflightGets = new Map<string, Promise<ApiResponse<unknown>>>();

function dedupedGet<T>(path: string, query?: Query): Promise<ApiResponse<T>> {
  const key = buildUrl(path, query);
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<ApiResponse<T>>;
  const p = request<T>(path, { method: 'GET', query }).finally(() => inflightGets.delete(key));
  inflightGets.set(key, p as Promise<ApiResponse<unknown>>);
  return p;
}

export const api = {
  get: <T>(path: string, query?: Query) => dedupedGet<T>(path, query),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  blob: (path: string, query?: Query) => requestBlob(path, query),
};
